/**
 * Barra superior: la campana de pendientes y, para el inquilino, su tarjeta de cuenta.
 * (El botón de ayuda que relanzaba el recorrido guiado se quitó: la aplicación no lleva tutorial.)
 * Alineada a la derecha, sobre la banda del titular de cada pantalla.
 *
 * En pantallas estrechas aparece a la IZQUIERDA el botón que abre la navegación, porque ahí
 * la barra lateral deja de estar fija. Vive en la barra superior y no flotando sobre el
 * contenido: un botón flotante tapa justo la primera fila de cualquier tabla.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { PendingTask } from '@naar/shared';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../api/client';
import { AccountMenu } from './AccountMenu';
import { Icon } from './Icon';
import mark from '../assets/naar-mark.png';
import styles from './TopBar.module.css';

interface TopBarProps {
  /** Abre la navegación cuando la barra lateral está plegada. */
  onOpenNav?: () => void;
  /**
   * Muestra aquí la tarjeta de cuenta.
   *
   * La usa el INQUILINO, que no tiene barra lateral: sin esto no había ni un solo sitio en
   * toda su sesión desde el que cerrarla. El resto de roles la siguen teniendo abajo del todo
   * en la barra lateral, que es donde la pone el maquetado.
   */
  showAccount?: boolean;
  /**
   * Campana de alertas con el número de alertas nuevas. Era una entrada más del menú; aquí
   * se ve desde cualquier pantalla y avisa sin tener que entrar. No para la inquilina: las
   * alertas son de la gestión.
   */
  showAlerts?: boolean;
}

/** Dónde están todos: la pestaña Pendientes de Seguimiento. */
const TODOS_LOS_PENDIENTES = '/seguimiento?tab=pendientes';

/** Cuántos se enseñan en el desplegable: los primeros, que ya vienen ordenados por urgencia. */
const EN_EL_DESPLEGABLE = 8;

/**
 * La campana: lo que hay pendiente, sin salir de la pantalla en la que estás.
 *
 * El número es lo VENCIDO y lo de ESTA SEMANA, lo que pide atención; lo de más adelante sale en
 * la lista pero no enciende nada. Cada fila lleva a donde se resuelve (el mes de la conciliación,
 * la ficha del inquilino…) y abajo, «Ver todos», a la pestaña Pendientes de Seguimiento.
 *
 * Se cierra con Esc (devolviendo el foco a la campana), al pulsar fuera y al elegir una.
 */
function AlertsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.pending(),
    queryFn: () => api.pending(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const count = data ? data.counts.vencido + data.counts.semana : 0;
  const tareas = (data?.tasks ?? []).filter((task) => task.bucket !== 'oportunidad');
  const visibles = tareas.slice(0, EN_EL_DESPLEGABLE);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function ir(to: string) {
    setOpen(false);
    navigate(to);
  }

  return (
    <div className={styles.bellWrap} ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={open ? `${styles.bell} ${styles.bellOpen}` : styles.bell}
        aria-label={count > 0 ? `Pendientes: ${count} para ya` : 'Pendientes'}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Pendientes"
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="bell" size={18} strokeWidth={2} />
        {count > 0 && <span className={styles.badge}>{count > 99 ? '99+' : count}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="Pendientes">
          <div className={styles.panelHead}>
            <span className={styles.panelTitle}>Pendientes</span>
            {count > 0 && <span className={styles.panelCount}>{count} para ya</span>}
          </div>

          {isPending ? (
            <p className={styles.panelEmpty}>Cargando…</p>
          ) : isError ? (
            <p className={styles.panelEmpty}>No se han podido cargar ahora mismo.</p>
          ) : tareas.length === 0 ? (
            <p className={styles.panelEmpty}>
              <Icon name="check" size={16} strokeWidth={2.4} />
              No hay nada pendiente.
            </p>
          ) : (
            <ul className={styles.panelList}>
              {visibles.map((task) => (
                <li key={task.id}>
                  <button type="button" className={styles.item} onClick={() => ir(task.link)}>
                    <span
                      className={styles.itemDot}
                      style={{ background: COLOR_DEL_PLAZO[task.bucket] }}
                      aria-hidden="true"
                    />
                    <span className={styles.itemBody}>
                      <span className={styles.itemTitle}>{task.title}</span>
                      {task.detail && <span className={styles.itemText}>{task.detail}</span>}
                      <span className={styles.itemMeta}>
                        <span className={styles.itemTag}>{ETIQUETA_DEL_PLAZO[task.bucket]}</span>
                        {task.property ? ` · ${task.property.name}` : ''}
                        {task.tenant ? ` · ${task.tenant.fullName}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <Link to={TODOS_LOS_PENDIENTES} className={styles.panelFoot} onClick={() => setOpen(false)}>
            {tareas.length > EN_EL_DESPLEGABLE ? `Ver los ${tareas.length} pendientes` : 'Ver todos los pendientes'}
            <Icon name="arrow-right" size={15} strokeWidth={2.2} />
          </Link>
        </div>
      )}
    </div>
  );
}

const COLOR_DEL_PLAZO: Record<PendingTask['bucket'], string> = {
  vencido: 'var(--naar-danger)',
  semana: '#b45309',
  mes: 'var(--naar-text-faint)',
  despues: 'var(--naar-hairline-strong)',
  oportunidad: 'var(--naar-hairline-strong)',
};

const ETIQUETA_DEL_PLAZO: Record<PendingTask['bucket'], string> = {
  vencido: 'Vencido',
  semana: 'Esta semana',
  mes: 'Este mes',
  despues: 'Más adelante',
  oportunidad: 'Oportunidad',
};

export function TopBar({ onOpenNav, showAccount = false, showAlerts = false }: TopBarProps) {
  const barRef = useRef<HTMLDivElement>(null);

  /**
   * La barra va anclada sobre la banda del titular, así que hay que reservarle su ancho REAL
   * a la derecha de cada cabecera. Ese ancho NO es constante: con la inquilina lleva además
   * la tarjeta de cuenta y mide más del doble que con el propietario. Con un número fijo en
   * el CSS acertabas en una pantalla y en la otra las acciones se metían debajo de la barra.
   *
   * De paso fija su `top` sobre el centro del <h1> DE ESA pantalla: el titular del panel es
   * de 38px y el del resto de 34px, así que un `top` constante quedaba 3px descuadrado en
   * todas menos en una.
   */
  useLayoutEffect(() => {
    const bar = barRef.current;
    const host = bar?.parentElement;
    if (!bar || !host) return;

    /* Se escribe solo cuando el valor cambia: `host` está observado y reescribir en cada
       notificación encadenaría medidas sin fin. */
    const write = (element: HTMLElement, property: string, value: string) => {
      if (element.style.getPropertyValue(property) !== value) {
        element.style.setProperty(property, value);
      }
    };

    const publish = () => {
      // Por debajo de 900px la barra vuelve al flujo: ni se ancla ni hay hueco que reservar.
      if (window.getComputedStyle(bar).position !== 'absolute') {
        write(bar, 'top', '');
        write(host, '--naar-topbar-space', '0px');
        return;
      }

      /*
       * Se lee con `offsetTop`/`offsetHeight` y NO con getBoundingClientRect: la pantalla
       * entra con una translación de 8px y el rectángulo la incluiría, dejando la barra
       * descuadrada para siempre a los 8px del primer fotograma. `offset*` es geometría de
       * maquetación pura, ajena a las transformaciones.
       */
      const heading = host.querySelector('h1');
      if (heading instanceof HTMLElement) {
        let top = heading.offsetHeight / 2;
        for (let node: HTMLElement | null = heading; node && node !== host; ) {
          top += node.offsetTop;
          node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null;
        }
        write(bar, 'top', `${Math.round(top)}px`);
      }

      write(
        host,
        '--naar-topbar-space',
        `${Math.ceil(bar.getBoundingClientRect().width) + 20}px`,
      );
    };

    /* Las medidas se agrupan en una sola tarea: el observador de mutaciones dispara en ráfaga
       cuando una pantalla cambia su esqueleto de carga por el contenido real.
       Con `setTimeout` y no `requestAnimationFrame` a propósito: en una pestaña de fondo los
       fotogramas se suspenden, y la barra se quedaría midiendo la pantalla anterior. */
    let pending = 0;
    const schedule = () => {
      if (pending) return;
      pending = window.setTimeout(() => {
        pending = 0;
        publish();
      }, 0);
    };

    publish();

    const sizes = new ResizeObserver(schedule);
    sizes.observe(bar);
    sizes.observe(host);
    /* Y el h1 aparece TARDE: mientras carga, la pantalla enseña un esqueleto que no lleva
       titular, así que medir solo al montar dejaba la barra en su sitio de reserva. */
    const contents = new MutationObserver(schedule);
    contents.observe(host, { childList: true, subtree: true });
    const wide = window.matchMedia('(min-width: 900px)');
    wide.addEventListener('change', schedule);
    window.addEventListener('resize', schedule);
    return () => {
      window.clearTimeout(pending);
      sizes.disconnect();
      contents.disconnect();
      wide.removeEventListener('change', schedule);
      window.removeEventListener('resize', schedule);
      bar.style.top = '';
      host.style.removeProperty('--naar-topbar-space');
    };
    /* Sin dependencias a propósito: quien manda son los observadores. Reengancharlos en cada
       cambio de ruta dejaba la medida a merced del orden de los efectos —y de si la pantalla
       ya había cambiado su esqueleto por el contenido— justo en el commit de la navegación. */
  }, []);

  return (
    <div className={styles.bar} ref={barRef}>
      {onOpenNav && (
        <button
          type="button"
          className={styles.menu}
          onClick={onOpenNav}
          aria-label="Abrir la navegación"
        >
          <Icon name="list" size={20} strokeWidth={2} />
        </button>
      )}
      {/* Solo en móvil y tableta, donde la barra lateral está escondida: la marca sigue a la
          vista y lleva al inicio. */}
      {onOpenNav && (
        <Link to="/" className={styles.mobileLogo} aria-label="NAAR · Inicio">
          <img src={mark} alt="" width={640} height={344} />
        </Link>
      )}
      {showAlerts && <AlertsBell />}
      {showAccount && <AccountMenu placement="bar" />}
    </div>
  );
}
